import React, { useState, useEffect } from "react";
import { Icon, Container } from "semantic-ui-react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSubscriptionStatus } from "../../../../hooks/useSubscriptionStatus";

import General from "./general/General";
import NotificationPreferences from "./general/NotificationPreferences";
import Billing from "./billing/Billing";
import DangerZone from "./dangerZone/DangerZone";

import "./Settings.css";

const TAB_IDS = ["general", "notifications", "billing", "dangerzone"];

const CompanySettings = () => {
  const { t } = useTranslation();
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const fixViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
      void document.body.offsetHeight;
      window.scrollTo(0, 0);
    };
    const timeoutId = setTimeout(() => {
      fixViewportHeight();
    }, 50);
    const handleResize = () => {
      setTimeout(fixViewportHeight, 100);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const allSettingsTabs = [
    { id: "general", name: t("app.settingsPage.general"), icon: "settings", color: "#667eea", paddingLeft: "0px" },
    { id: "notifications", name: t("app.settingsPage.notificationPreferences"), icon: "bell outline", color: "#f59e0b", paddingLeft: "2px" },
    { id: "billing", name: t("app.settingsPage.billing"), icon: "dollar sign", color: "#34d399", paddingLeft: "4%" },
    { id: "dangerzone", name: t("app.settingsPage.dangerZone"), icon: "warning circle", color: "#ff9a9e", paddingLeft: "5%" },
  ];

  const settingsTabs = isSuspended
    ? allSettingsTabs.filter((tab) => tab.id === "billing" || tab.id === "dangerzone")
    : allSettingsTabs;

  const parseTabFromLocation = () => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("tab");
    if (tabFromUrl === "departments") {
      return "general";
    }
    if (tabFromUrl && TAB_IDS.includes(tabFromUrl)) {
      if (isSuspended && tabFromUrl !== "billing" && tabFromUrl !== "dangerzone") {
        return "billing";
      }
      return tabFromUrl;
    }
    const savedTab = localStorage.getItem("companySettingsActiveTab");
    if (savedTab === "departments" || savedTab === "backup") {
      return "general";
    }
    if (savedTab && TAB_IDS.includes(savedTab)) {
      if (isSuspended && savedTab !== "billing" && savedTab !== "dangerzone") {
        return "billing";
      }
      return savedTab;
    }
    return isSuspended ? "billing" : "general";
  };

  const [activeTab, setActiveTab] = useState(parseTabFromLocation);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "departments") {
      history.replace("/settings/organization/departments");
      return;
    }
    if (tab === "backup") {
      history.replace("/settings/leave?tab=backup");
      return;
    }
    setActiveTab(parseTabFromLocation());
  }, [location.search, isSuspended, history]);

  useEffect(() => {
    if (isSuspended && activeTab !== "billing" && activeTab !== "dangerzone") {
      setActiveTab("billing");
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, activeTab, history]);

  const handleTabChange = (tabId) => {
    if (isSuspended && tabId !== "billing" && tabId !== "dangerzone") {
      setActiveTab("billing");
      history.replace("/settings/company?tab=billing");
      return;
    }
    setActiveTab(tabId);
    localStorage.setItem("companySettingsActiveTab", tabId);
    if (tabId === "general") {
      history.replace("/settings/company");
    } else {
      history.replace(`/settings/company?tab=${tabId}`);
    }
  };

  const renderContent = () => {
    if (isSuspended && activeTab !== "billing" && activeTab !== "dangerzone") {
      return <Billing />;
    }
    switch (activeTab) {
      case "general":
        return <General />;
      case "notifications":
        return <NotificationPreferences />;
      case "billing":
        return <Billing />;
      case "dangerzone":
        return <DangerZone />;
      default:
        return isSuspended ? <Billing /> : <General />;
    }
  };

  return (
    <Container className="py-4 px-3 px-md-4" fluid>
      <div className="settings-header">
        <div className="settings-header-content">
          <div className="settings-title-section">
            <div className="settings-icon-wrapper">
              <Icon name="building" size="large" style={{ color: "#ffffff" }} />
            </div>
            <div>
              <h1 className="settings-main-title">{t("app.settingsPage.companySettings")}</h1>
              <p className="settings-subtitle">{t("app.settingsPage.companySettingsSubtitle")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-simple-nav">
        <div className="nav-container">
          <div className="nav-scroll">
            {settingsTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`nav-tab ${activeTab === tab.id ? "active" : ""} ${tab.id === "dangerzone" ? "danger-zone-tab" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <div className="tab-icon" style={{ background: tab.color, paddingLeft: tab.paddingLeft }}>
                  <Icon name={tab.icon} />
                </div>
                <span className="tab-name">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="settings-main-content">
        <div className="settings-content">
          <div className="settings-content-card">{renderContent()}</div>
        </div>
      </div>
    </Container>
  );
};

export default CompanySettings;
